import elementVisibility from 'lib/elementVisibility';
import getBreakpointSize from 'lib/getBreakpointSize';
import consolv from 'lib/consolv';

const renderKeys = [
  'isEmpty',
  'serviceName',
  'slotContentChanged',
  // 'size',
  'advertiserId',
  'campaignId',
  'creativeId',
  'labelIds',
  'lineItemId',
  'sourceAgnosticCreativeId',
  'sourceAgnosticLineItemId',
  'isBackfill',
  'yieldGroupIds',
  'companyIds',
];

const slotFunctions = [
  'getAdUnitPath',
  'getTargeting',
  'setTargeting',
  'getTargetingMap',
  'clearTargeting',
];

function getIFrameVisibility(element) {
  const iframe = element && element.getElementsByTagName('iframe')[0];
  if (iframe) return elementVisibility(iframe);
  return null;
}

/**
 * A custom wrapper for the a DFP ad slot that exposes additional information and functionality.
 */
export default class DFPAdSlot {
  _MCP;

  _DFPSlot;

  _type;

  _id;

  _element;

  _creative = {
    isEmpty: true,
  };

  _impressionViewable = false;

  _meta = {
    stale: false,
    loaded: false,
  };

  _inViewPercentage = 0;

  _clearTargetingAfterRefresh = [];

  /**
   * @param {Object} params
   * @param {MCPInstance} params.MCP
   * @param {SlotInstance} params.slot Slot instance provided by GPT library event after
   * slot has been created
   * @param {Element} params.element DOM element for target ad slot
   * @param {SlotType} params.type The type of target ad slot
   * @param {MetaList} params.meta Initial meta to add to target ad slot
   */
  constructor({ MCP, slot: googleTagSlot, element, type, meta }) {
    this.handleSlotRenderEnded = this.handleSlotRenderEnded.bind(this);
    this.handleSlotVisibilityChanged = this.handleSlotVisibilityChanged.bind(this);
    this.handleImpressionViewable = this.handleImpressionViewable.bind(this);
    this.toPlainObject = this.toPlainObject.bind(this);
    this.clearTargetingAfterRefresh = this.clearTargetingAfterRefresh.bind(this);
    this.refresh = this.refresh.bind(this);
    this.resetSlot = this.resetSlot.bind(this);
    this.destroy = this.destroy.bind(this);
    this.getMetaData = this.getMetaData.bind(this);
    this.setMetaData = this.setMetaData.bind(this);
    this.removeMetaData = this.removeMetaData.bind(this);

    if (typeof googleTagSlot !== 'object') {
      throw new Error('This must be constructed with a DFP Slot object');
    }

    this._MCP = MCP;
    this._DFPSlot = googleTagSlot;
    this._id = this._DFPSlot.getSlotElementId();
    this._element = element;
    this._type = type;
    this._meta = { ...meta, ...this._meta };

    /**
     * @ignore
     */
    slotFunctions.forEach((property) => {
      this[property] = googleTagSlot[property].bind(googleTagSlot);
    });
  }

  /**
   * @private
   */
  handleSlotRenderEnded(e) {
    if (e.slot !== this._DFPSlot) return;

    const { slotDefinition } = this;

    renderKeys.forEach((key) => {
      this._creative[key] = e[key];
    });

    if (e.size) this._creative.size = e.size.join();
    this.setMetaData('creativeLoaded', true);
    this.setMetaData('renderedAt', new Date());
    this.setMetaData('loaded', true);
    this.setMetaData('stale', false);

    // EXPERIMENTAL
    if (slotDefinition.resizeContainer && e.size) {
      const [width, height] = e.size;
      this.element.style.width = `${width}px`;
      this.element.style.height = `${height}px`;
    }
    // END EXPERIMENTAL

    this._clearTargetingAfterRefresh.forEach((key) => { this.clearTargeting(key); });
    this._clearTargetingAfterRefresh = [];
  }

  /**
   * @private
   */
  handleSlotVisibilityChanged(e) {
    if (e.slot !== this._DFPSlot) return;
    this._inViewPercentage = e.inViewPercentage * 0.01;
  }

  /**
   * @private
   */
  handleImpressionViewable(e) {
    if (e.slot !== this._DFPSlot) return;
    this._impressionViewable = true;
  }

  toPlainObject() {
    const visibilityResults = elementVisibility(this.element);
    return {
      meta: this._meta,
      creative: this.creative,
      id: this.id,
      type: this.type,
      targeting: this.targeting,
      inViewPercentage: this.inViewPercentage,
      visibility: visibilityResults.visibility || 0,
      distance: visibilityResults.distance || Infinity,
      sizes: this.sizes,
    };
  }

  get adUnitPath() {
    return this.getAdUnitPath();
  }

  get DFPSlot() {
    return this._DFPSlot;
  }

  get element() {
    return this._element;
  }

  get creative() {
    return this._creative;
  }

  get meta() {
    return this._meta;
  }

  get id() {
    return this._id;
  }

  get type() {
    return this._type;
  }

  get targeting() {
    return this._DFPSlot.getTargetingMap();
  }

  get inViewPercentage() {
    return this._inViewPercentage;
  }

  get impressionViewable() {
    return this._impressionViewable;
  }

  get visibility() {
    return elementVisibility(this.element).visibility || 0;
  }

  get distanceFromViewport() {
    return elementVisibility(this.element).distance;
  }

  get slotDefinition() {
    const { slotDefinitions = {} } = this._MCP.Configurator.getConfiguration();
    return slotDefinitions[this.type];
  }

  get sizes() {
    const sizes = [];
    const slotSizes = this._DFPSlot.getSizes();
    for (let i = 0; i < slotSizes.length; i += 1) {
      if (slotSizes[i] === 'fluid') {
        sizes.push([Infinity, Infinity]);
      } else {
        sizes.push([slotSizes[i].getWidth(), slotSizes[i].getHeight()]);
      }
    }
    return sizes;
  }

  get sizeMap() {
    const breakpointSize = getBreakpointSize(window, this._MCP._breakpoints);
    const { slotDefinitions = {} } = this._MCP.Configurator.getConfiguration();
    const { type } = this;
    const sizeMap = slotDefinitions[type].sizes;

    const sizes = (sizeMap && sizeMap.responsive && sizeMap.responsive[breakpointSize])
      || sizeMap.default;

    return sizes.map((size) => {
      if (size === 'fluid') return 'fluid';
      return size.split(',').map(v => Number(v));
    });
  }

  setMetaData(key, value) {
    this._meta[key] = value;
  }

  getMetaData(key) {
    return this._meta[key];
  }

  removeMetaData(key) {
    delete this._meta[key];
  }

  clearTargetingAfterRefresh(arrayOfKeys) {
    if (!Array.isArray(arrayOfKeys)) return;
    this._clearTargetingAfterRefresh.push(...arrayOfKeys);
  }

  resetSlot() {
    this.removeMetaData('creativeLoaded');
    this._creative = {};
    this._inViewPercentage = 0;
    this._impressionViewable = false;
  }

  refresh(additionalTargeting = {}) {
    const { googletag } = window;
    const DFPSlot = this._DFPSlot;
    const oldTargeting = DFPSlot.getTargetingMap();

    Object.keys(additionalTargeting).forEach((key) => {
      DFPSlot.setTargeting(key, additionalTargeting[key]);
    });

    consolv.log('DFP: Direct Slot Refresh', this);
    googletag.pubads().refresh([DFPSlot]);

    Object.keys(additionalTargeting).forEach((key) => {
      if (oldTargeting[key]) {
        DFPSlot.setTargeting(key, additionalTargeting[key]);
      } else {
        DFPSlot.clearTargeting(key);
      }
    });

    this.resetSlot();
  }

  clear() {
    const { googletag } = window;
    googletag.pubads().clear([this._DFPSlot]);
  }

  destroy() {
    const { googletag } = window;
    googletag.destroySlots([this._DFPSlot]);
  }
}
